import clsx from 'clsx'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { Button } from '../../../../../../../../components/Button'
import { type InternationalBankFormValues } from './types/FormsSteps'

type InternationalBankStepFormProps = {
  isSubmitting: boolean
  onSubmit: (data: any) => void
  onCancel: () => void
}

export const InternationalBankStepForm = ({
  isSubmitting,
  onSubmit,
  onCancel,
}: InternationalBankStepFormProps) => {
  const { formatMessage } = useIntl()
  const { control, formState, handleSubmit } =
    useFormContext<InternationalBankFormValues>()

  return (
    <div className="flex flex-col w-full h-full">
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-[640px]">
        <Controller
          name="swiftCode"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <>
              <p className="block mb-1 text-xs text-grey-800 lg:mb-2 lg:text-base">
                {formatMessage({ id: 'SWIFT_CODE' })}
              </p>

              <input
                className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                onChange={onChange}
                value={value}
              />

              {error?.message && (
                <p className="mt-2 text-[#FF4949] text-xs">
                  {formatMessage({ id: `${error?.message}` })}
                </p>
              )}
            </>
          )}
        />

        <Controller
          name="bankAccountNumber"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <>
              <p className="block mb-1 text-xs text-grey-800 lg:mb-2 lg:text-base mt-3">
                {formatMessage({ id: 'ACCOUNT_NUMBER_OR_IBAN' })}
              </p>

              <input
                className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                onChange={onChange}
                value={value}
              />

              {error?.message && (
                <p className="mt-2 text-[#FF4949] text-xs">
                  {formatMessage({ id: `${error?.message}` })}
                </p>
              )}
            </>
          )}
        />

        <Controller
          name="internationalBankBranch"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <>
              <p className="block mb-1 text-xs text-grey-800 lg:mb-2 lg:text-base mt-3">
                {formatMessage({ id: 'INTERNATIONAL_BANK_BRANCH' })}
              </p>

              <input
                className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                onChange={onChange}
                value={value}
              />

              {error?.message && (
                <p className="mt-2 text-[#FF4949] text-xs">
                  {formatMessage({ id: `${error?.message}` })}
                </p>
              )}
            </>
          )}
        />

        <Controller
          name="amazonGiftCardEmail"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <>
              <p className="block mb-1 text-xs text-grey-800 lg:mb-2 lg:text-base mt-3">
                {formatMessage({ id: 'AMAZON_GIFT_CARD_EMAIL' })}
              </p>

              <input
                className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                onChange={onChange}
                value={value}
              />

              {error?.message && (
                <p className="mt-2 text-[#FF4949] text-xs">
                  {formatMessage({ id: `${error?.message}` })}
                </p>
              )}
            </>
          )}
        />

        <div
          className={clsx('mt-14', {
            'md:max-w-[284px] flex gap-2': Boolean(onCancel),
            'md:w-32': !onCancel,
          })}
        >
          <Button type="button" variant="outline" onClick={onCancel} fullWidth>
            {formatMessage({ id: 'CANCEL' })}
          </Button>

          <Button
            type="submit"
            fullWidth
            disabled={!formState.isValid}
            isLoading={isSubmitting}
          >
            {formatMessage({ id: 'SAVE' })}
          </Button>
        </div>
      </form>
    </div>
  )
}
